import { typeBasedStrategy } from './strategies.js';

/**
 * Deep equality check for JSON-like values
 * @param {*} a - First value
 * @param {*} b - Second value
 * @returns {boolean} - True if deeply equal
 */
export function deepEqual(a, b) {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (a === undefined || b === undefined) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return false;

  const isArrayA = Array.isArray(a);
  const isArrayB = Array.isArray(b);
  if (isArrayA !== isArrayB) return false;

  if (isArrayA) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }

  return true;
}

/**
 * Performs three-way merge of JSON-like values
 *
 * @param {*} local - Local state of the value
 * @param {*} master - Current state of the master branch
 * @param {*} ancestor - Common ancestor
 * @param {object} options - Configuration options
 * @param {object} options.mergeStrategies - Map of JSON paths to merge strategy functions
 * @param {Function} options.defaultStrategy - Default strategy to use when no custom strategy matches
 * @param {string} _path - Internal: current path for strategy lookup
 * @returns {*} - Merged value
 */
export function merge(local, master, ancestor, options = {}, _path = '') {
  let strategy = null;
  try {
    // 1. Check common strategy
    if (deepEqual(local, ancestor)) return master;
    if (deepEqual(master, ancestor)) return local;

    // 2. Compute strategy
    const { mergeStrategies, defaultStrategy } = options;
    let strategy = mergeStrategies?.[_path]; // Custom strategy for this path
    if (!strategy) strategy = defaultStrategy; // Fallback to default
    if (!strategy) strategy = typeBasedStrategy; // Fallback to type-based

    // 3. Craft mergeNested
    // Remove current path strategy to prevent infinite recursion
    const nestedStrategies = mergeStrategies ? { ...mergeStrategies } : undefined;
    if (nestedStrategies) {
      delete nestedStrategies[_path];
    }
    const nestedOptions = { ...options, mergeStrategies: nestedStrategies };

    const mergeNested = (localVal, masterVal, ancestorVal, childKey) => {
      const childPath =
        childKey === '' ? _path : _path ? `${_path}.${childKey}` : childKey;
      return merge(localVal, masterVal, ancestorVal, nestedOptions, childPath);
    };

    // 4. Call strategy
    return strategy(local, master, ancestor, mergeNested);
  } catch (exception) {
    console.error("Exception while merging", local, master, ancestor, options, _path, strategy, exception);
    return master;
  }
}

/**
 * Conflict handler for RxDB collections
 *
 * @param {object} options - Configuration options
 * @param {object} options.mergeStrategies - Map of JSON paths to merge strategy functions
 * @param {Function} options.defaultStrategy - Default strategy to use when no custom strategy matches
 * @returns {*} - Conflict handler
 */
export function conflictHandlerWithStrategies(options) {
  return {
    isEqual: (a, b) => {
      if(typeof a === 'object' && !Array.isArray(a) && a !== null && typeof b === 'object' && !Array.isArray(b) && b !== null){
        return RxDB.defaultConflictHandler.isEqual(a, b)
      }
      return deepEqual(a, b);
    },
    resolve: i => merge(i.newDocumentState, i.realMasterState, i.assumedMasterState, options)
  }
}